import { ArrowLeft, ArrowRight, CircleNotch, Sparkle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingLabels, WizardStep } from "./types";

export interface OnboardingActionBarProps {
  step: WizardStep;
  canAdvance: boolean;
  isProvisioning: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  labels: OnboardingLabels["actions"];
  className?: string;
}

export function OnboardingActionBar({
  step,
  canAdvance,
  isProvisioning,
  onBack,
  onNext,
  onSubmit,
  labels,
  className,
}: OnboardingActionBarProps) {
  const isLast = step === 3;

  return (
    <div
      className={cn(
        "sticky bottom-0 z-10 -mx-4 mt-8 flex items-center gap-3 border-t border-biz-border bg-biz-background/95 px-4 py-3 backdrop-blur",
        "sm:-mx-6 sm:px-6 sm:py-4",
        "[padding-bottom:calc(env(safe-area-inset-bottom)+12px)]",
        className,
      )}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={step === 1 || isProvisioning}
        className={cn(
          "inline-flex h-14 min-w-[7rem] items-center justify-center gap-2 rounded-md border border-biz-input bg-biz-background px-5 text-sm font-semibold text-biz-foreground transition-colors",
          "hover:bg-biz-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-40",
        )}
      >
        <ArrowLeft size={16} aria-hidden />
        {labels.back}
      </button>

      <div className="flex-1" />

      <button
        type="button"
        onClick={isLast ? onSubmit : onNext}
        disabled={!canAdvance || isProvisioning}
        aria-busy={isProvisioning}
        className={cn(
          "inline-flex h-16 min-w-[10rem] items-center justify-center gap-2 rounded-md bg-biz-primary px-6 text-base font-semibold text-biz-primary-foreground shadow-biz-md transition-all duration-150",
          "hover:bg-biz-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "active:scale-[0.98]",
        )}
      >
        {isProvisioning ? (
          <CircleNotch size={20} className="animate-spin" aria-hidden />
        ) : isLast ? (
          <Sparkle size={20} aria-hidden />
        ) : null}
        <span>{isLast ? labels.submit : labels.next}</span>
        {!isLast && !isProvisioning && <ArrowRight size={16} aria-hidden />}
      </button>
    </div>
  );
}
